import { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  InfoHint,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@bliks-io/ui';
import {
  CapacityPrioritySchema,
  type CapacityPriority,
  type PrivacyMode,
  type SupportedLanguage
} from '@bliks-io/contracts';

const CAPACITY_PRIORITY_KEYS: Record<CapacityPriority, { label: string; description: string }> = {
  'survey-first': {
    label: 'OrgSettingPage.capacityPrioritySurveyFirst',
    description: 'OrgSettingPage.capacityPrioritySurveyFirstDesc'
  },
  'manual-first': {
    label: 'OrgSettingPage.capacityPriorityManualFirst',
    description: 'OrgSettingPage.capacityPriorityManualFirstDesc'
  },
  'survey-only': {
    label: 'OrgSettingPage.capacityPrioritySurveyOnly',
    description: 'OrgSettingPage.capacityPrioritySurveyOnlyDesc'
  },
  'manual-only': {
    label: 'OrgSettingPage.capacityPriorityManualOnly',
    description: 'OrgSettingPage.capacityPriorityManualOnlyDesc'
  }
};

interface PreferencesCardProps {
  defaultLanguage: SupportedLanguage;
  recordingTime: number;
  reviewTimeframe: number;
  internalBillingRate: number;
  privacyMode: PrivacyMode;
  capacityPriority: CapacityPriority;
  onLanguageChange: (value: SupportedLanguage) => void;
  onRecordingTimeChange: (value: number) => void;
  onReviewTimeframeChange: (value: number) => void;
  onInternalBillingRateChange: (value: number) => void;
  onPrivacyModeChange: (value: PrivacyMode) => void;
  onCapacityPriorityChange: (value: CapacityPriority) => void;
  recordingTimeError?: string;
  reviewTimeframeError?: string;
  internalBillingRateError?: string;
  className?: string;
}

interface NumericSettingRowProps {
  label: string;
  value: number;
  unit: string;
  error?: string;
  /** Optional explanatory tooltip shown next to the label. */
  hint?: ReactNode;
  /** Receives the raw input string; the caller chooses how to coerce it (parseInt vs Number). */
  onChange: (rawValue: string) => void;
}

/** A settings row: left label, right-aligned numeric input filling a fixed column, trailing unit. */
function NumericSettingRow({ label, value, unit, error, hint, onChange }: NumericSettingRowProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <Label className="text-base font-medium">{label}</Label>
        {hint && <InfoHint content={hint} />}
      </div>
      <div className="flex w-[300px] items-center gap-2">
        <div className="flex flex-1 flex-col items-end">
          <Input
            className="w-full"
            type="number"
            min={0}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          />
          {error && <span className="mt-1 text-xs text-destructive">{error}</span>}
        </div>
        <span className="w-16 shrink-0 whitespace-nowrap text-sm text-muted-foreground">
          {unit}
        </span>
      </div>
    </div>
  );
}

export function PreferencesCard({
  defaultLanguage,
  recordingTime,
  reviewTimeframe,
  internalBillingRate,
  privacyMode,
  capacityPriority,
  onLanguageChange,
  onRecordingTimeChange,
  onReviewTimeframeChange,
  onInternalBillingRateChange,
  onPrivacyModeChange,
  onCapacityPriorityChange,
  recordingTimeError,
  reviewTimeframeError,
  internalBillingRateError,
  className
}: PreferencesCardProps) {
  const { t } = useTranslation();

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
          {t('OrgSettingPage.preferencesTitle')}
        </CardTitle>
        <CardDescription>{t('OrgSettingPage.preferencesDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Language */}
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">{t('OrgSettingPage.Language')}</Label>
          <Select
            value={defaultLanguage}
            onValueChange={(v) => onLanguageChange(v as SupportedLanguage)}
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">{t('OrgSettingPage.languageEnglish')}</SelectItem>
              <SelectItem value="de">{t('OrgSettingPage.languageGerman')}</SelectItem>
              <SelectItem value="hu">{t('OrgSettingPage.languageHungarian')}</SelectItem>
              <SelectItem value="fr">{t('OrgSettingPage.languageFrench')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Recording Time */}
        <NumericSettingRow
          label={t('OrgSettingPage.RecordingTime')}
          value={recordingTime}
          unit={t('OrgSettingPage.hours')}
          error={recordingTimeError}
          hint={t('OrgSettingPage.recordingTimeHint')}
          onChange={(raw) => onRecordingTimeChange(parseInt(raw))}
        />

        {/* Review Timeframe */}
        <NumericSettingRow
          label={t('OrgSettingPage.reviewTimeFrame')}
          value={reviewTimeframe}
          unit={t('OrgSettingPage.days')}
          error={reviewTimeframeError}
          hint={t('OrgSettingPage.reviewTimeFrameHint')}
          onChange={(raw) => onReviewTimeframeChange(parseInt(raw))}
        />

        {/* Internal Billing Rate (Verrechnungssatz) — Number, not parseInt, so decimal rates survive */}
        <NumericSettingRow
          label={t('OrgSettingPage.internalBillingRate')}
          value={internalBillingRate}
          unit={t('OrgSettingPage.internalBillingRateUnit')}
          error={internalBillingRateError}
          onChange={(raw) => onInternalBillingRateChange(Number(raw))}
        />

        {/* Capacity Source (Kapazitätsquelle) */}
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">{t('OrgSettingPage.capacityPriority')}</Label>
          <Combobox
            value={capacityPriority}
            onValueChange={(v) => onCapacityPriorityChange(v as CapacityPriority)}
          >
            <ComboboxTrigger className="w-[300px]">
              {t(CAPACITY_PRIORITY_KEYS[capacityPriority].label)}
            </ComboboxTrigger>
            <ComboboxContent>
              <ComboboxList>
                {CapacityPrioritySchema.options.map((option) => (
                  <ComboboxItem key={option} value={option}>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{t(CAPACITY_PRIORITY_KEYS[option].label)}</span>
                      <span className="text-xs text-muted-foreground">
                        {t(CAPACITY_PRIORITY_KEYS[option].description)}
                      </span>
                    </div>
                  </ComboboxItem>
                ))}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>

        {/* Privacy Mode */}
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">{t('OrgSettingPage.privacyMode')}</Label>
          <Combobox
            value={privacyMode}
            onValueChange={(v) => onPrivacyModeChange(v as PrivacyMode)}
          >
            <ComboboxTrigger className="w-[300px]">
              {privacyMode === 'full-transparency'
                ? t('OrgSettingPage.privacyFullTransparency')
                : t('OrgSettingPage.privacyNeedToKnow')}
            </ComboboxTrigger>
            <ComboboxContent>
              <ComboboxList>
                <ComboboxItem value="need-to-know">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{t('OrgSettingPage.privacyNeedToKnow')}</span>
                    <span className="text-xs text-muted-foreground">
                      {t('OrgSettingPage.privacyNeedToKnowDesc')}
                    </span>
                  </div>
                </ComboboxItem>
                <ComboboxItem value="full-transparency">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">
                      {t('OrgSettingPage.privacyFullTransparency')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('OrgSettingPage.privacyFullTransparencyDesc')}
                    </span>
                  </div>
                </ComboboxItem>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>
      </CardContent>
    </Card>
  );
}
